import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { KeyboardEvent } from 'react';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Returns the props needed to make a clickable table header (`<th>`) that
 * triggers sorting accessible: keyboard operable (Enter/Space), focusable,
 * and announced with the correct `aria-sort` state for screen readers.
 */
export function sortableHeaderProps(
  onActivate: () => void,
  isActive?: boolean,
  direction?: 'asc' | 'desc' | null
) {
  const ariaSort: 'ascending' | 'descending' | 'none' = isActive
    ? direction === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';

  return {
    onClick: onActivate,
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
    tabIndex: 0,
    role: 'button' as const,
    'aria-sort': ariaSort,
  };
}

// Display labels for workflow step type values (stored/matched in lowercase).
const STEP_TYPE_LABELS: Record<string, string> = {
  approval: 'Approval',
  fulfillment: 'Fulfillment',
  notification: 'Notification',
  review: 'Review',
  confirmation: 'Confirm Pickup',
};

export function getStepTypeLabel(stepType?: string | null): string {
  if (!stepType) return 'Approval';
  return STEP_TYPE_LABELS[stepType.toLowerCase()] || stepType;
}

export function formatDate(date: string | Date, format: 'short' | 'long' = 'short'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (format === 'long') {
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(amount);
}

/**
 * Formats a line item SKU for display. Some source SKUs embed a BPA/contract
 * number after a pipe (e.g. "M58525|47QSCC21A0004") which is dropped, and the
 * vendor prefix (Order_EntryAttributes "VendorPrefix") is prepended when present
 * (e.g. "MCAB-M58525").
 */
export function formatSkuDisplay(sku?: string | null, vendorPrefix?: string | null): string {
  if (!sku) return '';
  const baseSku = sku.split('|')[0];
  return vendorPrefix ? `${vendorPrefix}-${baseSku}` : baseSku;
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): T {
  let timeout: NodeJS.Timeout;
  
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
}

// User display utilities
export function getFullName(user: { 
  given_name?: string; 
  family_name?: string; 
  firstName?: string; 
  lastName?: string; 
  [key: string]: any;
} | null | undefined): string {
  if (!user) return 'User';
  
  // Prefer JWT claims (given_name, family_name) over database fields (firstName, lastName)
  const firstName = user.given_name || user.firstName || '';
  const lastName = user.family_name || user.lastName || '';
  
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || 'User';
}

export function getDisplayName(user: { 
  given_name?: string; 
  family_name?: string; 
  firstName?: string; 
  lastName?: string;
  preferred_username?: string;
  email?: string;
  [key: string]: any;
} | null | undefined): string {
  if (!user) return 'User';
  
  const fullName = getFullName(user);
  if (fullName !== 'User') {
    return fullName;
  }
  
  // Fallback to username or email if no name is available
  return user.preferred_username || user.email || 'User';
}

export function formatTimeDifference(milliseconds: number): string {
  if (milliseconds < 0) return '0m';
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${seconds}s`;
}
